import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  MarkEmailRead as ReadIcon,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { RTL } from "../../cache/CacheRtl";
import { useAdminMessages } from "../../contexts/AdminMessageContext";
import { useMessageContext } from "../../contexts/MessageContext";
import { borders, colors } from "../ui/colors";
import moment from "moment";
import "moment/locale/he";

moment.locale("he");

const MessageInboxDrawer = ({ open, onClose }) => {
  const { inbox, inboxLoading, refreshInbox, markRead, deleteReceipt } = useAdminMessages();
  const { notificationHandler } = useMessageContext();
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (open) {
      refreshInbox();
    } else {
      setExpandedId(null);
    }
  }, [open, refreshInbox]);

  const handleRowClick = (receipt) => {
    setExpandedId(prev => (prev === receipt.id ? null : receipt.id));
    if (!receipt.readAt) {
      markRead(receipt.id).catch(() => {});
    }
  };

  const handleDelete = (e, receiptId) => {
    e.stopPropagation();
    deleteReceipt(receiptId)
      .then(() => notificationHandler.success("ההודעה נמחקה"))
      .catch(() => notificationHandler.error("מחיקה נכשלה"));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={0}
      PaperProps={{
        sx: { width: { xs: "100%", sm: "420px" } },
      }}
    >
      <RTL>
        <Box dir="rtl" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: borders.default,
            backgroundColor: colors.bg,
          }}>
            <Typography sx={{ fontWeight: 700, fontSize: "18px", color: colors.textPrimary }}>
              הודעות מערכת
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {inboxLoading && inbox.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            ) : inbox.length === 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, color: colors.textMuted }}>
                <ReadIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography sx={{ fontSize: "14px" }}>אין הודעות</Typography>
              </Box>
            ) : (
              inbox.map((receipt) => {
                const isExpanded = expandedId === receipt.id;
                const isUnread = !receipt.readAt;
                return (
                  <Box
                    key={receipt.id}
                    onClick={() => handleRowClick(receipt)}
                    sx={{
                      cursor: "pointer",
                      px: 2,
                      py: 1.5,
                      borderBottom: borders.default,
                      backgroundColor: isUnread ? colors.primarySubtle : "transparent",
                      "&:hover": { backgroundColor: colors.bgHover },
                      "&:hover .delete-btn": { opacity: 1 },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isUnread && (
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: colors.error,
                          flexShrink: 0,
                        }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontWeight: isUnread ? 700 : 500,
                          fontSize: "14px",
                          color: colors.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {receipt.message?.title}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: colors.textMuted, mt: 0.25 }}>
                          {receipt.message?.senderName} · {receipt.message?.createdDate ? moment.unix(receipt.message.createdDate).fromNow() : ""}
                        </Typography>
                      </Box>
                      <Tooltip title="מחק">
                        <IconButton
                          size="small"
                          className="delete-btn"
                          onClick={(e) => handleDelete(e, receipt.id)}
                          sx={{ opacity: 0, transition: "opacity 0.15s", color: colors.textMuted }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {isExpanded && (
                      <Box sx={{ mt: 1.5, pl: isUnread ? 2 : 0 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Typography sx={{
                          fontSize: "13px",
                          color: colors.textSecondary,
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.6,
                        }}>
                          {receipt.message?.content}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </RTL>
    </Drawer>
  );
};

export default MessageInboxDrawer;
